import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { TrendingUp, TrendingDown, Calendar, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { TableCell, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { InstallmentSeries } from "@/hooks/useInstallments";
import { InstallmentProgress } from "./InstallmentProgress";

interface InstallmentRowProps {
  series: InstallmentSeries;
  onClick?: () => void;
}

export function InstallmentRow({ series, onClick }: InstallmentRowProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL"
    }).format(value);
  };

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return "-";
    return format(new Date(date), "dd/MM/yyyy", { locale: ptBR });
  };

  const hasOverdue = series.installments.some((i) => i.status === "OVERDUE" && !i.is_paid);

  return (
    <TableRow
      className={cn(
        "cursor-pointer",
        series.type === "income" ? "hover:border-l-4 hover:border-l-green-500" : "hover:border-l-4 hover:border-l-red-500"
      )}
      onClick={onClick}
    >
      {/* Description */}
      <TableCell>
        <div className="flex items-center gap-2">
          {series.type === "income" ? (
            <TrendingUp className="h-4 w-4 text-green-600 shrink-0" />
          ) : (
            <TrendingDown className="h-4 w-4 text-red-600 shrink-0" />
          )}
          <div>
            <div className="font-medium">{series.description}</div>
            <div className="text-sm text-muted-foreground">{series.category}</div>
          </div>
        </div>
      </TableCell>

      {/* Type */}
      <TableCell>
        <Badge
          variant="outline"
          className={cn(
            series.type === "income"
              ? "bg-green-100 text-green-700 border-green-200 hover:bg-green-200"
              : "bg-red-100 text-red-700 border-red-200 hover:bg-red-200"
          )}
        >
          {series.total_installments}x
        </Badge>
      </TableCell>

      {/* Total Value */}
      <TableCell className="text-right font-medium">
        {formatCurrency(series.amount)}
      </TableCell>

      {/* Installment Value */}
      <TableCell className="text-right text-muted-foreground">
        {formatCurrency(series.installment_amount)}
      </TableCell>

      {/* Progress */}
      <TableCell>
        <InstallmentProgress series={series} size="sm" showPercentage />
      </TableCell>

      {/* Dates */}
      <TableCell className="text-sm text-muted-foreground">
        <div className="flex items-center gap-1">
          <Calendar className="h-3 w-3 shrink-0" />
          <span>{formatDate(series.date)}</span>
        </div>
        {series.next_due_date && series.status === "active" && (
          <div className={cn(
            "flex items-center gap-1 mt-1",
            hasOverdue && "text-red-600 font-medium"
          )}>
            {hasOverdue && <AlertCircle className="h-3 w-3 shrink-0" />}
            <span>Próxima: {formatDate(series.next_due_date)}</span>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}
